/**
 * Cart store (M3 slice). Holds lines a rep has gathered — re-added from order
 * history now, added from item detail in M4. The margin dial, customer binding,
 * and checkout (post to Spire) land in M4; this is deliberately just the line
 * collection + persistence so "re-add to cart" works end to end today.
 */

import { create } from 'zustand';

export interface CartItem {
  /** `${partNo}|${whseCode}|${sellMeasure ?? ''}` — merges repeat adds of the same part+UOM. */
  key: string;
  partNo: string;
  whseCode: string;
  description: string | null;
  qty: number;
  /** Decimal as string. */
  unitPrice: string;
  /** Selling unit of measure (e.g. EA, BX). Posted to Spire as the line's sellMeasure. */
  sellMeasure?: string;
  /** Item cost when known (added from item detail by a cost-permitted user) —
   * powers the per-line margin badge. Absent for re-ordered lines. */
  unitCost?: string;
  /** True when the rep explicitly set this price (margin dial, manual edit,
   * ladder pick). Overridden lines post to Spire with userPrice=true; untouched
   * lines post WITHOUT a price so Spire's pricing engine (matrix/level/promo)
   * prices them — `unitPrice` is then only the display estimate. */
  priceOverridden?: boolean;
  /** Where the display estimate came from ('contract' | 'level' | 'default' |
   * 'manual') — drives the price-source badge. */
  priceSource?: string;
}

export interface AddCartLine {
  partNo: string;
  whseCode: string;
  description: string | null;
  unitPrice: string;
  sellMeasure?: string;
  qty?: number;
  unitCost?: string;
  priceOverridden?: boolean;
  priceSource?: string;
}

/** The customer an order is being built for — carried from "Start an order". */
export interface SelectedCustomer {
  spireId: number;
  customerNo: string;
  name: string;
  /** Pricing tier — lets the item screen show this customer's price level. */
  sellLevel: number;
}

interface CartState {
  items: CartItem[];
  /** Customer the current order is bound to (persisted across navigation). */
  selectedCustomer: SelectedCustomer | null;
  setSelectedCustomer(c: SelectedCustomer): void;
  clearSelectedCustomer(): void;
  add(line: AddCartLine): void;
  setQty(key: string, qty: number): void;
  setPrice(key: string, unitPrice: string): void;
  remove(key: string): void;
  clear(): void;
}

const STORAGE_KEY = 'kdental:cart';
const CUST_KEY = 'kdental:cart-customer';

function loadCustomer(): SelectedCustomer | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(CUST_KEY);
    return raw ? (JSON.parse(raw) as SelectedCustomer) : null;
  } catch {
    return null;
  }
}
function persistCustomer(c: SelectedCustomer | null): void {
  try {
    if (c) window.localStorage.setItem(CUST_KEY, JSON.stringify(c));
    else window.localStorage.removeItem(CUST_KEY);
  } catch {
    /* private mode — ignore */
  }
}

function load(): CartItem[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as CartItem[]) : [];
  } catch {
    return [];
  }
}

function persist(items: CartItem[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    /* private mode — ignore */
  }
}

export const useCart = create<CartState>((set, get) => ({
  items: load(),
  selectedCustomer: loadCustomer(),

  setSelectedCustomer(c) {
    set({ selectedCustomer: c });
    persistCustomer(c);
  },
  clearSelectedCustomer() {
    set({ selectedCustomer: null });
    persistCustomer(null);
  },

  add(line) {
    const key = `${line.partNo}|${line.whseCode}|${line.sellMeasure ?? ''}`;
    const qty = line.qty ?? 1;
    const existing = get().items.find((i) => i.key === key);
    const items = existing
      ? get().items.map((i) => (i.key === key ? { ...i, qty: i.qty + qty } : i))
      : [
          ...get().items,
          {
            key,
            partNo: line.partNo,
            whseCode: line.whseCode,
            description: line.description,
            qty,
            unitPrice: line.unitPrice,
            sellMeasure: line.sellMeasure,
            unitCost: line.unitCost,
            priceOverridden: line.priceOverridden ?? false,
            priceSource: line.priceSource,
          },
        ];
    set({ items });
    persist(items);
  },

  setQty(key, qty) {
    const items = get()
      .items.map((i) => (i.key === key ? { ...i, qty: Math.max(1, Math.floor(qty)) } : i));
    set({ items });
    persist(items);
  },

  setPrice(key, unitPrice) {
    // A manual price edit is an explicit override — the line will post to
    // Spire with userPrice=true instead of being priced by Spire's engine.
    const items = get().items.map((i) =>
      i.key === key ? { ...i, unitPrice, priceOverridden: true, priceSource: 'manual' } : i,
    );
    set({ items });
    persist(items);
  },

  remove(key) {
    const items = get().items.filter((i) => i.key !== key);
    set({ items });
    persist(items);
  },

  clear() {
    set({ items: [] });
    persist([]);
  },
}));
